import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Check, Pencil, Plus, Trash2, X } from "lucide-react"
import { useState } from "react"

import { AddHoursDialog } from "@/pages/Team/Employees/AddHoursDialog"
import { Avatar, Kpi, KpiStrip, PageHeader, StatusBadge } from "@/components/steel"
import { Button } from "@/components/ui/button"
import { DataTable, Row, RowList, Td, Th, Thead, Tr } from "@/components/steel/table"
import { EditEmployeeDialog } from "@/components/edit-employee-dialog"
import { EditTimeEntryDialog } from "@/components/edit-time-entry-dialog"
import { formatCurrency } from "@/lib/utils"
import type { AssignedProject, EmployeeProp, TimeEntry } from "@/pages/Team/Employees/tabs/types"

type Props = {
  assignedProjects: AssignedProject[]
  employee: EmployeeProp
  timeEntries: TimeEntry[]
}

function formatDate(value: string) {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export default function EmployeeShow({ assignedProjects, employee, timeEntries }: Props) {
  const [editOpen, setEditOpen] = useState(false)
  const [logOpen, setLogOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)

  return (
    <>
      <Head title={employee.name} />

      <PageHeader
        leading={
          <>
            <Button variant="ghost" size="icon-sm" asChild>
              <Link href="/team/employees">
                <ArrowLeft />
              </Link>
            </Button>
            <Avatar initials={employee.avatar} />
          </>
        }
        title={employee.name}
        meta={
          <span className="flex items-center gap-2.5">
            {employee.role} · {employee.department}
            <StatusBadge status={employee.status} />
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete "${employee.name}"? Their assignments and time entries go with them.`,
                  )
                ) {
                  router.delete(`/employees/${employee.id}`)
                }
              }}
            >
              <Trash2 />
              Delete
            </Button>
          </>
        }
      />

      <KpiStrip>
        <Kpi compact label="Base rate" value={formatCurrency(employee.hourlyRate)} hint="Per hour" />
        <Kpi compact label="Hours logged" value={`${employee.totalHours}h`} hint="Across all projects" />
        <Kpi
          compact
          label="Labor value"
          value={formatCurrency(employee.totalEarnings)}
          hint={`${employee.totalHours}h at project rates`}
        />
        <Kpi
          compact
          label="Projects"
          value={assignedProjects.length}
          hint={assignedProjects.length ? assignedProjects.map((p) => p.id).join(", ") : "None"}
        />
      </KpiStrip>

      <div className="grid lg:grid-cols-2">
        <div className="border-border px-6 py-4 lg:border-r">
          <h2 className="mb-3 text-title font-semibold">Per-project rates</h2>
          {assignedProjects.length === 0 ? (
            <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
              Not assigned to any project.
            </p>
          ) : (
            <DataTable minWidth={420}>
              <Thead>
                <Th>Project</Th>
                <Th align="right">Pay</Th>
                <Th align="right">Bill</Th>
                <Th align="right">Margin</Th>
              </Thead>
              <tbody>
                {assignedProjects.map((project) => {
                  const pay = project.payRate ?? employee.hourlyRate
                  const bill = project.billRate ?? 0
                  return (
                    <Tr key={project.id} onClick={() => router.visit(`/projects/${project.id}`)}>
                      <Td>
                        <span className="block text-body font-medium">{project.name.split(" - ")[0]}</span>
                        <span className="block text-meta text-muted-foreground">
                          {project.role ?? employee.role}
                        </span>
                      </Td>
                      <Td align="right" num>
                        {formatCurrency(pay)}
                      </Td>
                      <Td align="right" num>
                        {bill ? formatCurrency(bill) : "—"}
                      </Td>
                      <Td align="right" num accent>
                        {bill ? formatCurrency(bill - pay) : "—"}
                      </Td>
                    </Tr>
                  )
                })}
              </tbody>
            </DataTable>
          )}
        </div>

        <div className="px-6 py-4 pb-7">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-title font-semibold">Recent time</h2>
            <Button variant="outline" size="sm" onClick={() => setLogOpen(true)}>
              <Plus />
              Log hours
            </Button>
          </div>

          {timeEntries.length === 0 ? (
            <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
              No time entries yet.
            </p>
          ) : (
            <RowList>
              {timeEntries.map((entry) => (
                <Row key={entry.id}>
                  <span className="num w-[62px] shrink-0 text-label text-muted-foreground">
                    {formatDate(entry.date)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body">{entry.description}</span>
                    <span className="block truncate text-meta text-muted-foreground">
                      {entry.projectName.split(" - ")[0]}
                    </span>
                  </span>
                  <span className="num text-body font-medium">{entry.hours}h</span>
                  <StatusBadge status={entry.status} />
                  <span className="flex shrink-0 items-center gap-0.5">
                    <Button variant="ghost" size="icon-sm" onClick={() => setEditingEntry(entry)}>
                      <Pencil />
                    </Button>
                    {entry.status === "pending" ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() =>
                            router.patch(`/time_entries/${entry.id}`, { status: "approved" }, { preserveScroll: true })
                          }
                        >
                          <Check />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() =>
                            router.patch(`/time_entries/${entry.id}`, { status: "rejected" }, { preserveScroll: true })
                          }
                        >
                          <X />
                        </Button>
                      </>
                    ) : null}
                  </span>
                </Row>
              ))}
            </RowList>
          )}
        </div>
      </div>

      <AddHoursDialog
        employeeId={employee.id}
        onOpenChange={setLogOpen}
        open={logOpen}
        projects={assignedProjects}
      />

      {editingEntry ? (
        <EditTimeEntryDialog
          key={editingEntry.id}
          entry={editingEntry}
          onOpenChange={(open) => !open && setEditingEntry(null)}
          open
        />
      ) : null}

      <EditEmployeeDialog
        key={`${employee.name}-${employee.hourlyRate}-${employee.status}`}
        employee={employee}
        onOpenChange={setEditOpen}
        open={editOpen}
      />
    </>
  )
}
